import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createFlashDeal, updateFlashDeal, updateFormData, clearFormData } from '../../../store/slices/flashDealsSlice';
import { AppDispatch, RootState } from '../../../store/store';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { api } from '../../../services/api';
import { useFormik } from 'formik';
import { flashDealsValidationSchema } from './flashDealsValidation';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
} from '@mui/material';
import './FlashDeals.css';
 
interface FlashDealFormProps {
  mode: 'create' | 'edit';
  initialData?: any;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onCancel?: () => void;
  onCloseTab?: (tabId: string) => void;
}
 
interface CarOption {
  id: number;
  plateNumber: string;
  carType: {
    makeCompany: string;
    model: string;
    id: number;
    capacity: number;
  };
}
 
const locationOptions = ["Mumbai Airport", "Mumbai", "Pune"];
 
const FlashDealForm: React.FC<FlashDealFormProps> = ({
  mode,
  initialData,
  onSuccess,
  onError,
  onCancel,
  onCloseTab
}) => {
  const formDataFromStore = useSelector((state: RootState) => state.flashDeals.formData);
  const loading = useSelector((state: RootState) => state.flashDeals.loading);
  const dispatch = useDispatch<AppDispatch>();
 
  const [successMessage, setSuccessMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [carTypes, setCarTypes] = useState<CarOption[]>([]);
  const [loadingCars, setLoadingCars] = useState(false);
  const [isEditable, setIsEditable] = useState(mode === 'create'); // Read-only by default in edit mode
  const [showCreationSuccess, setShowCreationSuccess] = useState(false);
   
  // Show success message when switching to edit mode after creation
  useEffect(() => {
    if (mode === 'edit' && initialData && !isEditable) {
      // Check if this is a newly loaded edit (not from user clicking edit button)
      const isNewlyCreated = sessionStorage.getItem(`flashdeal_created_${initialData.dealCode}`);
      if (isNewlyCreated) {
        setSuccessMessage('Flash deal created successfully!');
        setShowCreationSuccess(true);
        sessionStorage.removeItem(`flashdeal_created_${initialData.dealCode}`);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
          setShowCreationSuccess(false);
        }, 5000);
      }
    }
  }, [mode, initialData, isEditable]);

  // Determine initial values based on mode
  const getInitialValues = () => {
    if (mode === 'edit' && initialData) {
      return {
        dealCode: initialData.dealCode || '',
        dealType: initialData.dealType || 1,
        fromSource: initialData.fromSource || '',
        toDestination: initialData.toDestination || '',
        pickupAddress: initialData.pickupAddress || '',
        carType: { id: initialData.carType?.id || 0 },
        pickupStartDatetime: initialData.pickupStartDatetime ? new Date(initialData.pickupStartDatetime) : null,
        pickupEndDatetime: initialData.pickupEndDatetime ? new Date(initialData.pickupEndDatetime) : null,
        dealStartDatetime: initialData.dealStartDatetime ? new Date(initialData.dealStartDatetime) : null,
        dealEndDatetime: initialData.dealEndDatetime ? new Date(initialData.dealEndDatetime) : null,
        basePrice: initialData.basePrice || null,
        offerPrice: initialData.offerPrice || null,
        availableSeats: initialData.availableSeats || null,
        linkedTripCode: initialData.linkedTripCode || '',
        isActive: initialData.isActive ?? true
      };
    }
    return formDataFromStore;
  };
 
  const formik = useFormik({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validationSchema: flashDealsValidationSchema,
    onSubmit: async (values) => {
      setSubmitError('');
      setSuccessMessage('');
      try {
        const formatDateTime = (date: Date | null): string | null => {
          if (!date) return null;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        };
 
        const submitData = {
          dealCode: mode === 'edit' ? initialData?.dealCode : generateDealCode(),
          dealType: values.dealType,
          fromSource: values.fromSource,
          toDestination: values.toDestination,
          pickupAddress: values.pickupAddress,
          carType: { id: values.carType.id },
          pickupStartDatetime: formatDateTime(values.pickupStartDatetime),
          pickupEndDatetime: formatDateTime(values.pickupEndDatetime),
          dealStartDatetime: formatDateTime(values.dealStartDatetime),
          dealEndDatetime: formatDateTime(values.dealEndDatetime),
          basePrice: values.dealType === 1 ? values.basePrice : null,
          offerPrice: values.dealType === 1 ? values.offerPrice : null,
          availableSeats: values.dealType === 2 ? values.availableSeats : null,
          linkedTripCode: values.linkedTripCode || null,
          isActive: values.isActive
        };
 
        let result: any;
        if (mode === 'edit' && initialData?.id) {
          result = await dispatch(updateFlashDeal({ id: initialData.id, flashDealData: submitData })).unwrap();
          setSuccessMessage(result?.message || 'Flash deal updated successfully!');
          setIsEditable(false); // Set back to read-only after successful update
          if (onSuccess) onSuccess({ result, mode: 'edit' });
        } else {
          result = await dispatch(createFlashDeal(submitData)).unwrap();
          const createdDealCode = result?.data?.dealCode || submitData.dealCode;
          
          // Mark this flash deal as newly created in sessionStorage
          sessionStorage.setItem(`flashdeal_created_${createdDealCode}`, 'true');
          
          dispatch(clearFormData());
          if (onSuccess) onSuccess({ result, mode: 'create' });
        }
      } catch (error: any) {
        console.error('Error saving flash deal:', error);
        const errorMessage = error?.message || error || 'Failed to save flash deal. Please try again.';
        setSubmitError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    }
  });

  // Add this useEffect to watch for dealType changes and reset availableSeats
  useEffect(() => {
    if (formik.values.dealType === 1) {
      formik.setFieldValue('availableSeats', null);
    } else if (formik.values.dealType === 2) {
      formik.setFieldValue('basePrice', null);
      formik.setFieldValue('offerPrice', null);
    }
  }, [formik.values.dealType]);

  // Persist form data for create mode only
  useEffect(() => {
    if (mode === 'create' && !initialData) {
      const timeoutId = setTimeout(() => {
        dispatch(updateFormData(formik.values));
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, mode, initialData, dispatch]);
 
  // Fetch car types on mount
  useEffect(() => {
    const fetchCarTypes = async () => {
      try {
        setLoadingCars(true);
        const response = await api.getAllCars();
        const carsData: CarOption[] = response?.data?.data || [];
        setCarTypes(carsData);
      } catch (error) {
        console.error('Error fetching car types:', error);
        setSubmitError('Failed to load car types');
      } finally {
        setLoadingCars(false);
      }
    };
    fetchCarTypes();
  }, []);
 
  const generateDealCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };
 
  const uniqueCarTypes = Array.from(
    new Map(carTypes.map(car => [car.carType.id, car.carType])).values()
  );
 
  const handleCancel = () => {
    if (mode === 'create') {
      dispatch(clearFormData());
      formik.resetForm();
    } else if (mode === 'edit') {
      setIsEditable(false); // Reset to read-only mode
      formik.resetForm(); // Reset any unsaved changes
    }
    if (onCancel) onCancel();
  };

  const handleEditClick = () => {
    setIsEditable(true);
  };

  // Determine if fields should be disabled
  const isFieldDisabled = mode === 'edit' && !isEditable;
 
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="flash-deals-container">
        <Box className="flash-deals-wrapper">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" className="flash-deals-title">
              {mode === 'edit' ? `Edit Flash Deal - ${initialData?.dealCode || ''}` : 'Create New Flash Deal'}
            </Typography>
            <Box sx={{display: 'flex', gap: 2}}>
               {/* {mode === 'edit' && !isEditable && (<Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={()=> {setIsEditable(false)}}
              >
                <CancelIcon sx={{mr: 1}}/>
                Cancel
              </Button>)} */}
            {mode === 'edit' && !isEditable && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon sx={{mr:1}}/>}
                onClick={handleEditClick}
              >
                Edit
              </Button>
            )}
            </Box>
          </Box>
 
          {successMessage && (
            <Alert severity="success" className="flash-deals-alert-success">
              {successMessage}
            </Alert>
          )}
 
          {submitError && (
            <Alert severity="error" className="flash-deals-alert-error">
              {submitError}
            </Alert>
          )}
 
          <form onSubmit={formik.handleSubmit}>
            <Box className="flash-deals-form">
              {/* Row 1: Deal Type & From Location */}
              <Box className="flash-deals-row">
                <Box className="flash-deals-field">
                  <FormControl fullWidth size="small" error={formik.touched.dealType && Boolean(formik.errors.dealType)} disabled={isFieldDisabled}>
                    <InputLabel className="flash-deals-select-label">Deal Type</InputLabel>
                    <Select
                      name="dealType"
                      value={formik.values.dealType}
                      onChange={formik.handleChange}
                      label="Deal Type"
                      className="flash-deals-select"
                      MenuProps={{ className: 'flash-deals-menu' }}
                    >
                      <MenuItem value={1}>One Way</MenuItem>
                      <MenuItem value={2}>Shared Trip</MenuItem>
                    </Select>
                    {formik.touched.dealType && formik.errors.dealType && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {String(formik.errors.dealType)}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                <Box className="flash-deals-field">
                  <Autocomplete
                    disableClearable
                    disabled={isFieldDisabled}
                    options={locationOptions}
                    value={formik.values.fromSource}
                    onChange={(_, value) => formik.setFieldValue('fromSource', value || '')}
                    size="small"
                    className="flash-deals-autocomplete"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="From Location"
                        className="flash-deals-input"
                        error={formik.touched.fromSource && Boolean(formik.errors.fromSource)}
                        helperText={formik.touched.fromSource && formik.errors.fromSource ? String(formik.errors.fromSource) : ''}
                      />
                    )}
                    ListboxProps={{ className: 'flash-deals-autocomplete-listbox' }}
                  />
                </Box>
              </Box>
 
              {/* Row 2: To Destination & Pickup Address */}
              <Box className="flash-deals-row">
                <Box className="flash-deals-field">
                  <Autocomplete
                    disableClearable
                    disabled={isFieldDisabled}
                    options={locationOptions}
                    value={formik.values.toDestination}
                    onChange={(_, value) => formik.setFieldValue('toDestination', value || '')}
                    size="small"
                    className="flash-deals-autocomplete"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="To Location"
                        className="flash-deals-input"
                        error={formik.touched.toDestination && Boolean(formik.errors.toDestination)}
                        helperText={formik.touched.toDestination && formik.errors.toDestination ? String(formik.errors.toDestination) : ''}
                      />
                    )}
                    ListboxProps={{ className: 'flash-deals-autocomplete-listbox' }}
                  />
                </Box>
                <Box className="flash-deals-field">
                  <TextField
                    fullWidth
                    name="pickupAddress"
                    label="Pickup Address"
                    value={formik.values.pickupAddress}
                    onChange={formik.handleChange}
                    size="small"
                    disabled={isFieldDisabled}
                    className="flash-deals-input"
                    error={formik.touched.pickupAddress && Boolean(formik.errors.pickupAddress)}
                    inputProps={{ maxLength: 30 }}
                    helperText={formik.touched.pickupAddress && formik.errors.pickupAddress ? String(formik.errors.pickupAddress) : ''}
                  />
                </Box>
              </Box>
 
              {/* Row 3: Car Type & Available Seats */}
              <Box className="flash-deals-row">
                <Box className="flash-deals-field">
                  <FormControl fullWidth size="small" error={formik.touched.carType?.id && Boolean(formik.errors.carType?.id)} disabled={isFieldDisabled}>
                    <InputLabel className="flash-deals-select-label">Car Type</InputLabel>
                    <Select
                      value={formik.values.carType.id || ''}
                      onChange={(e) => formik.setFieldValue('carType', { id: e.target.value as number })}
                      label="Car Type"
                      className="flash-deals-select"
                      MenuProps={{ className: 'flash-deals-menu' }}
                      disabled={isFieldDisabled || loadingCars}
                    >
                      <MenuItem value="" disabled>Select Car Type</MenuItem>
                      {uniqueCarTypes.length > 0 ? (
                        uniqueCarTypes.map((carType) => (
                          <MenuItem key={carType.id} value={carType.id}>
                            {carType.makeCompany} {carType.model} | {carType.capacity - 1}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>No car types available</MenuItem>
                      )}
                    </Select>
                    {formik.touched.carType?.id && formik.errors.carType?.id && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {String(formik.errors.carType.id)}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                <Box className="flash-deals-field">
                  <TextField
                    fullWidth
                    type="number"
                    name="availableSeats"
                    label="Available Seats"
                    value={formik.values.availableSeats || ''}
                    onChange={formik.handleChange}
                    onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                    size="small"
                    disabled={isFieldDisabled || formik.values.dealType === 1} // Disable for One Way deals
                    InputProps={{ inputProps: { min: 1, max: 6 } }}
                    className="flash-deals-input"
                    error={formik.touched.availableSeats && Boolean(formik.errors.availableSeats)}
                    helperText={formik.touched.availableSeats && formik.errors.availableSeats ? String(formik.errors.availableSeats) : ''}
                  />
                </Box>
              </Box>
 
              {/* Row 4: Pickup Start & End DateTime */}
              <Box className="flash-deals-row">
                <Box className="flash-deals-field">
                  <DateTimePicker
                    label="Pickup Start Time"
                    value={formik.values.pickupStartDatetime}
                    onChange={(value) => formik.setFieldValue('pickupStartDatetime', value)}
                    format="dd/MM/yyyy hh:mm a"
                    disabled={isFieldDisabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        className: 'flash-deals-input',
                        error: formik.touched.pickupStartDatetime && Boolean(formik.errors.pickupStartDatetime),
                        helperText: formik.touched.pickupStartDatetime && formik.errors.pickupStartDatetime ? String(formik.errors.pickupStartDatetime) : ''
                      },
                      popper: { className: 'flash-deals-datepicker' }
                    }}
                  />
                </Box>
                <Box className="flash-deals-field">
                  <DateTimePicker
                    label="Pickup End Time"
                    value={formik.values.pickupEndDatetime}
                    onChange={(value) => formik.setFieldValue('pickupEndDatetime', value)}
                    format="dd/MM/yyyy hh:mm a"
                    disabled={isFieldDisabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        className: 'flash-deals-input',
                        error: formik.touched.pickupEndDatetime && Boolean(formik.errors.pickupEndDatetime),
                        helperText: formik.touched.pickupEndDatetime && formik.errors.pickupEndDatetime ? String(formik.errors.pickupEndDatetime) : ''
                      },
                      popper: { className: 'flash-deals-datepicker' }
                    }}
                  />
                </Box>
              </Box>
 
              {/* Row 5: Deal Start & End DateTime */}
              <Box className="flash-deals-row">
                <Box className="flash-deals-field">
                  <DateTimePicker
                    label="Deal Start Date"
                    value={formik.values.dealStartDatetime}
                    onChange={(value) => formik.setFieldValue('dealStartDatetime', value)}
                    format="dd/MM/yyyy hh:mm a"
                    disabled={isFieldDisabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        className: 'flash-deals-input',
                        error: formik.touched.dealStartDatetime && Boolean(formik.errors.dealStartDatetime),
                        helperText: formik.touched.dealStartDatetime && formik.errors.dealStartDatetime ? String(formik.errors.dealStartDatetime) : ''
                      },
                      popper: { className: 'flash-deals-datepicker' }
                    }}
                  />
                </Box>
                <Box className="flash-deals-field">
                  <DateTimePicker
                    label="Deal End Date"
                    value={formik.values.dealEndDatetime}
                    onChange={(value) => formik.setFieldValue('dealEndDatetime', value)}
                    format="dd/MM/yyyy hh:mm a"
                    disabled={isFieldDisabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        className: 'flash-deals-input',
                        error: formik.touched.dealEndDatetime && Boolean(formik.errors.dealEndDatetime),
                        helperText: formik.touched.dealEndDatetime && formik.errors.dealEndDatetime ? String(formik.errors.dealEndDatetime) : ''
                      },
                      popper: { className: 'flash-deals-datepicker' }
                    }}
                  />
                </Box>
              </Box>
 
              {/* Row 6: Base Price & Offer Price */}
              <Box className="flash-deals-row">
                <Box className="flash-deals-field">
                  <TextField
                    fullWidth
                    type="number"
                    name="basePrice"
                    label="Base Price (₹)"
                    value={formik.values.basePrice || ''}
                    onChange={formik.handleChange}
                    size="small"
                    disabled={isFieldDisabled || formik.values.dealType === 2} // Disable for One Way deals
                    onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    className="flash-deals-input"
                    error={formik.touched.basePrice && Boolean(formik.errors.basePrice)}
                    helperText={formik.touched.basePrice && formik.errors.basePrice ? String(formik.errors.basePrice) : ''}
                  />
                </Box>
                <Box className="flash-deals-field">
                  <TextField
                    fullWidth
                    type="number"
                    name="offerPrice"
                    label="Offer Price (₹)"
                    value={formik.values.offerPrice || ''}
                    onChange={formik.handleChange}
                    onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                    size="small"
                    disabled={isFieldDisabled || formik.values.dealType === 2} // Disable for One Way deals
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    className="flash-deals-input"
                    error={formik.touched.offerPrice && Boolean(formik.errors.offerPrice)}
                    helperText={formik.touched.offerPrice && formik.errors.offerPrice ? String(formik.errors.offerPrice) : ''}
                  />
                </Box>
              </Box>
 
              {/* Row 7: Linked Trip Code & Status */}
              <Box className="flash-deals-row">
                <Box className="flash-deals-field">
                  <TextField
                    fullWidth
                    name="linkedTripCode"
                    label="Linked Trip Code (Optional)"
                    value={formik.values.linkedTripCode}
                    onChange={formik.handleChange}
                    size="small"
                    disabled={isFieldDisabled}
                    className="flash-deals-input"
                  />
                </Box>
                <Box className="flash-deals-field">
                  <FormControl fullWidth size="small" disabled={isFieldDisabled}>
                    <InputLabel className="flash-deals-select-label">Status</InputLabel>
                    <Select
                      name="isActive"
                      value={formik.values.isActive ? 'active' : 'inactive'}
                      onChange={(e) => formik.setFieldValue('isActive', e.target.value === 'active')}
                      label="Status"
                      className="flash-deals-select"
                      MenuProps={{ className: 'flash-deals-menu' }}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">In Active</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
 
              {/* Action Buttons */}
              <Box>
                <Box className="flash-deals-actions">
                  {/* { (isEditable || mode !== 'edit') && ( */}
                    <Button
                    type="button"
                    variant="outlined"
                    color="error"
                    onClick={handleCancel}
                  >
                    <CancelIcon sx={{mr:1}}/>
                    Close
                  </Button>
                {/* // )} */}
                {isEditable && <Button sx={{mx:1}}
                    type ="button"
                    variant="contained"
                    color="secondary"
                    onClick={()=> {setIsEditable(false)}}
                  >
                    <CancelIcon sx={{mr:1}}/>
                    Cancel
                  </Button>
}
                  {(isEditable || mode !== 'edit') && (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading || formik.isSubmitting}
                      className="flash-deals-btn-submit"
                    >
                      <SaveIcon sx={{mr:1}}/>
                      {mode === 'create' ? 'Create' : 'Update'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </form>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
 
export default FlashDealForm;